import Application from './Application';
import common from './common';
import PreloaderScene from './scenes/PreloaderScene';

export default class Main {
	/**
     * @param {number} [width=1024]
     * @param {number} [height=768]
     */
	constructor(width = 1024, height = 768) {
		this._width = width;
		this._height = height;
		this._app = null;
		this._preloader = null;
	}
    
	init() {
		P3.assets.audioFileTypes = ['ogg', 'mp3'];
		P3.assets.signals.audioFile.add(P3.audio.addSound, P3.audio);

		PIXI.settings.RETINA_PREFIX = /\_(?=[^_]*$)(.+)x/;	//eslint-disable-line no-useless-escape
		PIXI.settings.MIPMAP_TEXTURES = false;

		typeof TweenMax !== 'undefined' && (TweenMax.defaultOverwrite = 'none');

		const options = {
			width: this._width,
			height: this._height,
			holderId: 'game',
			portrait: false,
			rotateImageUrl: './assets/images/localisation/rotate_en.jpg',
			rotateImageColor: '#000000',
			aspectRatioFillHeight: true
		};

		const view = new P3.View(options);
		view.signals.ready.once(function(canvas) {
			const options = {};
			options.view = canvas;
			options.transparent = true;
			options.antialias = false;
			options.preserveDrawingBuffer = false;
			options.resolution = 1;

			const stage = new PIXI.Container();
			common.stage = stage;

			P3.input.init(stage, canvas);

			if (P3.utils.isCocoonJS()) {
				stage.scale.x = window.innerHeight / options.height;
				stage.scale.y = window.innerHeight / options.height;
			}

			const renderer = !P3.utils.isCocoonJS() ?
				PIXI.autoDetectRenderer(this._width, this._height, options) :
				new PIXI.WebGLRenderer(window.innerWidth, window.innerHeight, options);
			renderer.backgroundColor = 0xFFFFFF;
			common.renderer = renderer;

			common.stage.addChild(P3.scene.view);

			const transitions = new PIXI.Container();
			common.stage.addChild(transitions);
			common.transitions = transitions;

			const ticker = new P3.Ticker();
			ticker.init(function() {
				P3.scene.update();
				P3.input.update();
				P3.utils.TWEEN.update();     // comment this line out if you are using TweenMax
			}, function() {
				renderer.render(stage);
			}, this);
			common.ticker = ticker;

			this.loadPreAssets();
		}, this);
		view.signals.resize.add(this.onCanvasResize, this);
		common.view = view;

		this.loadConfig(function() {
			view._options.rotateImageUrl = `./assets/images/localisation/rotate_${common.config.language}.jpg`;
			view.init();
		}, this);    // we load the config before we do anything
	}

	loadConfig(callback, scope) {
		scope = scope || window;
		P3.assets.addFiles([{name: 'config', url: 'config' + '.json'}], './');
		P3.assets.signals.complete.once(function() {
			common.config = P3.assets.json('config');
			common.config.language = window.__language;
			callback && callback.call(scope);
		}, this);
		P3.assets.load();
	}
    
	loadPreAssets() {
		const files = [
			{name: 'loading_ar', url: 'images/localisation/loading_ar' + '.png'},
			{name: 'splash_1x', url: 'images/splash_1x' + '.json'},
			{name: 'buttons_1x', url: 'images/buttons_1x' + '.json'},
			{name: 'strings', url: 'data/strings' + '.json'},
			{name: 'bg_preloader', url: 'images/bg_preloader' + '.jpg'},
			{name: 'ed_72', url: 'fonts/' + 'ed_72' + '.fnt'},
			{name: 'grilledcheese_56', url: 'fonts/' + 'grilledcheese_56' + '.fnt'},
			{name: 'ed_90_colours', url: 'fonts/' + 'ed_90_colours' + '.fnt'},
			{name: 'preloader_1x', url: 'images/preloader_1x' + '.json'}
		];
		if (files.length) {
			P3.assets.addFiles(files, './assets/');
			P3.assets.signals.complete.once(this.loadAssets, this);
			P3.assets.load();
		} else this.loadAssets();
	}
    
	loadAssets() {
		const files = [
			// comic 0
			{name: 'tutorial_scene0', url: 'spine/tutorial' + '.json'},
			{name: 'tutorial_scene0_pencil', url: 'spine/tutorial_pencil' + '.json'},
			{name: 'tutorial_scene0_data', url: 'data/tutorial_scene0' + '.json'},
			// comic 1
			{name: 'jerry_scene1', url: 'spine/jerry_scene1' + '.json'},
			{name: 'jerry_scene1_pencil', url: 'spine/jerry_scene1_pencil' + '.json'},
			{name: 'jerry_scene1_data', url: 'data/jerry_scene1' + '.json'},

			// comic 2
			{name: 'tom_scene2', url: 'spine/tom_scene2' + '.json'},
			{name: 'tom_scene2_pencil', url: 'spine/tom_scene2_pencil' + '.json'},
			{name: 'tom_scene2_data', url: 'data/tom_scene2' + '.json'},

			// comic 3
			{name: 'jerry_scene3', url: 'spine/jerry_scene3' + '.json'},
			{name: 'jerry_scene3_pencil', url: 'spine/jerry_scene3_pencil' + '.json'},
			{name: 'jerry_scene3_data', url: 'data/jerry_scene3' + '.json'},

			// comic 4
			{name: 'tom_scene4', url: 'spine/tom_scene4' + '.json'},
			{name: 'tom_scene4_pencil', url: 'spine/tom_scene4_pencil' + '.json'},
			{name: 'tom_scene4_data', url: 'data/tom_scene4' + '.json'},

			// comic 5
			{name: 'spike_scene5', url: 'spine/spike_scene5' + '.json'},
			{name: 'spike_scene5_pencil', url: 'spine/spike_scene5_pencil' + '.json'},
			{name: 'spike_scene5_data', url: 'data/spike_scene5' + '.json'},

			{name: 'tom_scene5', url: 'spine/tom_scene5' + '.json'},
			{name: 'tom_scene5_pencil', url: 'spine/tom_scene5_pencil' + '.json'},
			{name: 'tom_scene5_data', url: 'data/tom_scene5' + '.json'},

			// comic 6
			{name: 'tom_scene6', url: 'spine/tom_scene6' + '.json'},
			{name: 'tom_scene6_pencil', url: 'spine/tom_scene6_pencil' + '.json'},
			{name: 'tom_scene6_data', url: 'data/tom_scene6' + '.json'},

			{name: 'jerry_scene6', url: 'spine/jerry_scene6' + '.json'},
			{name: 'jerry_scene6_pencil', url: 'spine/jerry_scene6_pencil' + '.json'},
			{name: 'jerry_scene6_data', url: 'data/jerry_scene6' + '.json'},

			// comic 7
			{name: 'tom_scene7', url: 'spine/tom_scene7' + '.json'},
			{name: 'tom_scene7_pencil', url: 'spine/tom_scene7_pencil' + '.json'},
			{name: 'tom_scene7_data', url: 'data/tom_scene7' + '.json'},

			{name: 'jerry_scene7', url: 'spine/jerry_scene7' + '.json'},
			{name: 'jerry_scene7_pencil', url: 'spine/jerry_scene7_pencil' + '.json'},
			{name: 'jerry_scene7_data', url: 'data/jerry_scene7' + '.json'},

			//eraser
			{name: 'rubberSpine', url: 'spine/rubber' + '.json'},
			{name: 'rubberFX', url: 'particles/rubber' + '.json'},
            
			// Comic Files
			{name: 'comic0', url: 'data/comics/comic0' + '.json'},
			{name: 'comic1', url: 'data/comics/comic1' + '.json'},
			{name: 'comic2', url: 'data/comics/comic2' + '.json'},
			{name: 'comic3', url: 'data/comics/comic3' + '.json'},
			{name: 'comic4', url: 'data/comics/comic4' + '.json'},
			{name: 'comic5', url: 'data/comics/comic5' + '.json'},
			{name: 'comic6', url: 'data/comics/comic6' + '.json'},
			{name: 'comic7', url: 'data/comics/comic7' + '.json'},

			// Texture Sheets
			{name: 'ui_1x', url: 'images/ui_1x' + '.json'},
			{name: 'game_assets_0_1x', url: 'images/game_assets_0_1x' + '.json'},
			{name: 'bg_splash', url: 'images/bg_splash' + '.jpg'},
			{name: 'bg_game', url: 'images/bg_game' + '.jpg'},
			{name: 'bg_levelselect', url: 'images/bg_levelselect' + '.jpg'},
			{name: 'title_ar', url: 'images/localisation/title_ar' + '.png'},

			// Comic BGs
			{name: 'scene_tutorial', url: 'images/scene_tutorial' + '.jpg'},
			{name: 'scene1', url: 'images/scene1' + '.jpg'},
			{name: 'scene2', url: 'images/scene2' + '.jpg'},
			{name: 'scene3', url: 'images/scene3' + '.jpg'},
			{name: 'scene4', url: 'images/scene4' + '.jpg'},
			{name: 'scene5', url: 'images/scene5' + '.jpg'},
			{name: 'scene6', url: 'images/scene6' + '.jpg'},
			{name: 'scene7', url: 'images/scene7' + '.jpg'},

			// SFX
			{name: 'sfx_btn_ui_commit_00', url: 'audio/' + 'sfx_btn_ui_commit_00' + P3.assets.audioFileExt},
			{name: 'sfx_btn_ui_next_drawing_00', url: 'audio/' + 'sfx_btn_ui_next_drawing_00' + P3.assets.audioFileExt},
			{name: 'sfx_btn_ui_prev_drawing_00', url: 'audio/' + 'sfx_btn_ui_prev_drawing_00' + P3.assets.audioFileExt},
			{name: 'sfx_btn_ui_print_00', url: 'audio/' + 'sfx_btn_ui_print_00' + P3.assets.audioFileExt},
			{name: 'sfx_btn_ui_repeatstep_00', url: 'audio/' + 'sfx_btn_ui_repeatstep_00' + P3.assets.audioFileExt},
			{name: 'sfx_btn_ui_trash_00', url: 'audio/' + 'sfx_btn_ui_trash_00' + P3.assets.audioFileExt},
			{name: 'sfx_drawing_fill_00', url: 'audio/' + 'sfx_drawing_fill_00' + P3.assets.audioFileExt},
			{name: 'sfx_drawing_loop_00', url: 'audio/' + 'sfx_drawing_loop_00' + P3.assets.audioFileExt},

			// Music
			{name: 'music_menu_loop_00', url: 'audio/' + 'music_menu_loop_00' + P3.assets.audioFileExt},
			{name: 'music_drawing_loop_00', url: 'audio/' + 'music_drawing_loop_00' + P3.assets.audioFileExt},
			{name: 'music_complete_sting_02', url: 'audio/' + 'music_complete_sting_02' + P3.assets.audioFileExt},
			{name: 'music_complete_sting_01', url: 'audio/' + 'music_complete_sting_01' + P3.assets.audioFileExt},
			{name: 'music_complete_sting_00', url: 'audio/' + 'music_complete_sting_00' + P3.assets.audioFileExt}

		];
		if (files.length) {
			common.config.strings = P3.assets.json('strings');

			this._preloader = new PreloaderScene();
			P3.scene.show(this._preloader);

			P3.assets.addFiles(files, './assets/');
			P3.assets.signals.progress.add(this.onLoadingProgress, this);
			P3.assets.signals.complete.once(this.onLoadingCompleted, this);
			P3.assets.load();
		} else this.start();
	}

	start() {
		this._app = new Application();
		this._app.init();
	}

	onLoadingProgress(event) {
		this._preloader && (this._preloader.loaded = event.progress / 100);
		//console.log("loading:", event.progress);
	}

	onLoadingCompleted() {
		this._preloader && (this._preloader.loaded = 1);
		//this._preloader.signals.mobileLoad.add(() => {
			this.start();
		//});
	}

	onCanvasResize(correct) {
		if (correct) {
			common.renderer.resize(P3.View.width, P3.View.height);
			if (P3.scene) {
				P3.scene.resize();

				for (let i = 0; i < common.transitions.children.length; ++i) {
					const transition = common.transitions.children[i];
					transition.resize && transition.resize();
				}
			}
		}
	}
}
